import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Upload, Save, LogOut, Mail, BarChart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import './Dashboard.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  'Fashion', 'Beauty', 'Fitness', 'Travel', 'Food', 'Tech', 
  'Gaming', 'Lifestyle', 'Business', 'Education', 'Music', 'Art'
];

const InfluencerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/influencer/profile`);
      setProfile(response.data);
      setImagePreview(response.data.profile_picture_url || response.data.profile_image_url);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) {
      toast.error('Please select an image first');
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await axios.post(`${API_URL}/api/influencer/upload-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Profile picture updated!');
      setImageFile(null);
      fetchProfile();
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryToggle = (category) => {
    setProfile(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setLoading(true);
      await axios.put(`${API_URL}/api/influencer/profile`, profile);
      toast.success('Profile updated successfully!');
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (!profile) {
    return (
      <div className="dashboard-loading">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <h2>Influencer Dashboard</h2>
          <p>{user?.name}</p>
        </div>

        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
            data-testid="profile-tab"
          >
            <User size={20} />
            <span>Profile</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
            data-testid="analytics-tab"
          >
            <BarChart size={20} />
            <span>Analytics</span>
          </button>
          <button
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
            data-testid="messages-tab"
          >
            <Mail size={20} />
            <span>Messages</span>
          </button>
        </nav>

        <div className="sidebar-footer">
          <Button onClick={() => navigate('/')} variant="ghost" className="nav-btn">
            Back to Home
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="nav-btn logout-btn">
            <LogOut size={18} />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {activeTab === 'profile' && (
          <div className="dashboard-content">
            <h1 className="content-title">Profile Management</h1>

            {/* Profile Picture Upload */}
            <div className="profile-picture-section">
              <div className="picture-preview">
                {imagePreview ? (
                  <img src={imagePreview} alt="Profile" />
                ) : (
                  <div className="placeholder-avatar">
                    <User size={64} />
                  </div>
                )}
              </div>
              <div className="picture-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  id="picture-upload"
                  style={{ display: 'none' }}
                />
                <Button
                  onClick={() => document.getElementById('picture-upload').click()}
                  variant="outline"
                  className="upload-btn"
                >
                  <Upload size={18} />
                  Choose Photo
                </Button>
                {imageFile && (
                  <Button
                    onClick={handleImageUpload}
                    disabled={loading}
                    className="save-picture-btn"
                  >
                    {loading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                )}
              </div>
            </div>

            {/* Profile Form */}
            <div className="profile-form">
              <div className="form-group">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => setProfile({...profile, name: e.target.value})}
                  data-testid="name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={profile.email}
                  disabled
                  data-testid="email-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="bio">Bio</Label>
                <Textarea
                  id="bio"
                  value={profile.bio}
                  onChange={(e) => setProfile({...profile, bio: e.target.value})}
                  rows={4}
                  data-testid="bio-input"
                />
              </div>

              <div className="form-section">
                <Label>Categories</Label>
                <div className="categories-grid">
                  {CATEGORIES.map((category) => (
                    <label key={category} className="category-item">
                      <Checkbox
                        checked={profile.categories.includes(category)}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-section">
                <Label>Social Media Handles</Label>
                <div className="social-inputs">
                  <Input
                    placeholder="Instagram"
                    value={profile.social_handles.instagram || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, instagram: e.target.value}})}
                  />
                  <Input
                    placeholder="YouTube"
                    value={profile.social_handles.youtube || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, youtube: e.target.value}})}
                  />
                  <Input
                    placeholder="TikTok"
                    value={profile.social_handles.tiktok || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, tiktok: e.target.value}})}
                  />
                  <Input
                    placeholder="X (Twitter)"
                    value={profile.social_handles.x || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, x: e.target.value}})}
                  />
                  <Input
                    placeholder="Facebook"
                    value={profile.social_handles.facebook || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, facebook: e.target.value}})}
                  />
                  <Input
                    placeholder="Pinterest"
                    value={profile.social_handles.pinterest || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, pinterest: e.target.value}})}
                  />
                  <Input
                    placeholder="Podcast URL"
                    value={profile.social_handles.podcast || ''}
                    onChange={(e) => setProfile({...profile, social_handles: {...profile.social_handles, podcast: e.target.value}})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="availability-item">
                  <Checkbox
                    checked={profile.availability}
                    onCheckedChange={(checked) => setProfile({...profile, availability: checked})}
                  />
                  <span>Available for collaborations</span>
                </label>
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={loading}
                className="save-profile-btn"
                data-testid="save-profile-btn"
              >
                <Save size={18} />
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="dashboard-content">
            <h1 className="content-title">Analytics</h1>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">0</div>
                <div className="stat-label">Profile Views</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">0</div>
                <div className="stat-label">Contact Requests</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">0</div>
                <div className="stat-label">Collaborations</div>
              </div>
            </div>
            <p className="coming-soon">Detailed analytics coming soon!</p>
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="dashboard-content">
            <h1 className="content-title">Messages</h1>
            <div className="empty-state">
              <Mail size={48} />
              <p>No messages yet</p>
              <p className="empty-subtitle">You'll receive messages here when brands contact you</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default InfluencerDashboard;